import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function Profile({ user, setUser }) {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState({ name: "", profile_pic: "" });

  // Charger les données du profil
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) {
        console.log("Aucun utilisateur chargé, en attente...");
        setIsLoaded(true);
        return;
      }
      try {
        const response = await axios.get(`${API_BASE_URL}/users/${user.id}`, {
          headers: { Authorization: `Bearer ${user.access_token}` },
        });
        console.log("Données du profil:", response.data);
        setProfileData(response.data);
        setEditedData({
          name: response.data.name || "",
          profile_pic: response.data.profile_pic || "",
        });
      } catch (error) {
        console.error("Erreur lors de la récupération du profil :", error);
        if (error.response?.status === 401) {
          setUser(null);
          localStorage.removeItem("user");
          navigate("/login");
        }
      } finally {
        setIsLoaded(true);
      }
    };
    fetchData();
  }, [user, navigate, setUser]);

  // Initialisation d'AOS
  useEffect(() => {
    AOS.init({ duration: 1200, once: true });
    return () => {
      AOS.refresh();
    };
  }, []);

  useEffect(() => {
    if (isLoaded && profileData) {
      AOS.refresh();
    }
  }, [isLoaded, profileData]);

  // Fonction de déconnexion
  const handleLogout = () => {
    localStorage.removeItem("user");
    setUser(null);
    navigate("/login");
  };

  // Fonction de suppression du compte
  const handleDeleteAccount = async () => {
    if (
      window.confirm(
        "Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible."
      )
    ) {
      try {
        await axios.delete(`${API_BASE_URL}/users/${user.id}`, {
          headers: { Authorization: `Bearer ${user.access_token}` },
        });
        localStorage.removeItem("user");
        setUser(null);
        navigate("/login");
      } catch (error) {
        console.error("Erreur lors de la suppression du compte :", error);
        alert("Une erreur s'est produite lors de la suppression du compte.");
      }
    }
  };

  // Activer le mode édition
  const handleEdit = () => {
    setEditMode(true);
  };

  // Annuler l'édition
  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedData({
      name: profileData.name || "",
      profile_pic: profileData.profile_pic || "",
    });
  };

  // Enregistrer les modifications
  const handleSaveEdit = async () => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/users/${user.id}`,
        editedData,
        { headers: { Authorization: `Bearer ${user.access_token}` } }
      );
      setProfileData(response.data);
      // Mettre à jour l'utilisateur localement (si nécessaire)
      const updatedUser = {
        ...user,
        name: response.data.name,
        profile_pic: response.data.profile_pic,
      };
      setUser(updatedUser);
      setEditMode(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour du profil :", error);
      alert("Une erreur s'est produite lors de la mise à jour du profil.");
    }
  };

  // Gestion des champs de saisie en mode édition
  const handleChange = (e) => {
    setEditedData({ ...editedData, [e.target.name]: e.target.value });
  };

  if (!isLoaded) {
    return <div style={loadingStyle}>Chargement...</div>;
  }

  if (isLoaded && !profileData) {
    return <div style={loadingStyle}>Erreur de chargement des données</div>;
  }

  return (
    <div style={pageStyle}>
      {/* Bandeau en haut */}
      <div style={headerStyle}>
        <span style={logoStyle} onClick={() => navigate("/")} role="button">
          Dansy
        </span>
        <h1 style={titleStyle}>Mon Profil</h1>
      </div>

      {/* Contenu principal */}
      <div style={profileContainerStyle} data-aos="fade-up">
        <div style={profileImageContainerStyle}>
          <img
            src={profileData.profile_pic?.replace("=s96-c", "=s200-c")}
            alt="Profile"
            style={profileImageStyle}
          />
        </div>
        <div style={infoContainerStyle}>
          {editMode ? (
            <>
              <p>
                <strong>Nom :</strong>{" "}
                <input
                  type="text"
                  name="name"
                  value={editedData.name}
                  onChange={handleChange}
                  style={{ width: "100%", padding: "5px", fontSize: "1rem" }}
                />
              </p>
              <p>
                <strong>URL de l'image :</strong>{" "}
                <input
                  type="text"
                  name="profile_pic"
                  value={editedData.profile_pic}
                  onChange={handleChange}
                  style={{ width: "100%", padding: "5px", fontSize: "1rem" }}
                />
              </p>
            </>
          ) : (
            <>
              <p>
                <strong>Nom :</strong> {profileData.name || "Non disponible"}
              </p>
              <p>
                <strong>Email :</strong> {profileData.email || "Non disponible"}
              </p>
              <p>
                <strong>Date de création :</strong>{" "}
                {profileData.created_at
                  ? new Date(profileData.created_at).toLocaleString()
                  : "Non disponible"}
              </p>
              <p>
                <strong>Dernière connexion :</strong>{" "}
                {profileData.last_login
                  ? new Date(profileData.last_login).toLocaleString()
                  : "Non disponible"}
              </p>
            </>
          )}
        </div>

        {editMode ? (
          // En mode édition, on affiche uniquement les boutons Enregistrer et Annuler
          <div style={editButtonsContainerStyle}>
            <button onClick={handleSaveEdit} style={saveButtonStyle}>
              Enregistrer
            </button>
            <button onClick={handleCancelEdit} style={cancelButtonStyle}>
              Annuler
            </button>
          </div>
        ) : (
          // En mode affichage, on affiche les boutons habituels
          <>
            <div style={buttonContainerStyle}>
              <button onClick={() => navigate("/sessions")} style={buttonStyle}>
                Mes sessions
              </button>
              <button onClick={handleEdit} style={editButtonStyle}>
                Modifier
              </button>
            </div>
            <div style={singleButtonContainerStyle}>
              <button onClick={handleLogout} style={logoutButtonStyle}>
                Déconnexion
              </button>
            </div>
            <div style={singleButtonContainerStyle}>
              <button onClick={handleDeleteAccount} style={deleteAccountButtonStyle}>
                Supprimer mon compte
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* === Styles === */
const pageStyle = {
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  overflowX: "hidden",
  position: "relative",
  minHeight: "100vh",
  backgroundColor: "#fff",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
};

const headerStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(135deg, #fff0f3, #ffd6dd)",
  color: "#000",
  padding: "15px",
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
  height: "30px",
  zIndex: 100,
};

const logoStyle = {
  position: "absolute",
  left: "20px",
  fontSize: "1.7rem",
  fontFamily: "'Shirkhand', cursive",
  color: "#ff477e",
  fontWeight: "bold",
  cursor: "pointer",
};

const titleStyle = {
  fontSize: "1.5rem",
  fontWeight: 700,
};

const profileContainerStyle = {
  marginTop: "80px",
  backgroundColor: "#fff",
  borderRadius: "35px",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  padding: "20px",
  textAlign: "center",
  maxWidth: "400px",
  width: "100%",
};

const profileImageContainerStyle = {
  display: "flex",
  justifyContent: "center",
  marginBottom: "20px",
};

const profileImageStyle = {
  width: "120px",
  height: "120px",
  borderRadius: "50%",
  border: "3px solid #ff477e",
};

const infoContainerStyle = {
  fontSize: "16px",
  color: "#555",
  textAlign: "left",
  padding: "10px",
};

const buttonContainerStyle = {
  display: "flex",
  justifyContent: "space-between",
  marginTop: "20px",
};

const editButtonsContainerStyle = {
  display: "flex",
  justifyContent: "space-between",
  marginTop: "20px",
};

/* Styles pour les boutons en mode affichage */
const buttonStyle = {
  width: "48%",
  padding: "10px",
  backgroundColor: "#ff477e",
  color: "#fff",
  border: "none",
  borderRadius: "25px",
  fontSize: "16px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};

const editButtonStyle = {
  ...buttonStyle,
  backgroundColor: "#A8A8A8",
};

const singleButtonContainerStyle = {
  width: "100%",
  marginTop: "20px",
};

const logoutButtonStyle = {
  width: "100%",
  padding: "10px",
  backgroundColor: "#555",
  color: "#fff",
  border: "none",
  borderRadius: "25px",
  fontSize: "16px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};

const deleteAccountButtonStyle = {
  width: "100%",
  padding: "10px",
  backgroundColor: "#ff4d4d",
  color: "#fff",
  border: "none",
  borderRadius: "25px",
  fontSize: "16px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};

/* Nouveaux styles pour les boutons en mode édition */
const saveButtonStyle = {
  width: "48%",
  padding: "10px",
  backgroundColor: "#ff477e", // Rose pour Enregistrer
  color: "#fff",
  border: "none",
  borderRadius: "25px",
  fontSize: "16px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};

const cancelButtonStyle = {
  width: "48%",
  padding: "10px",
  backgroundColor: "#A8A8A8", // Gris pour Annuler
  color: "#fff",
  border: "none",
  borderRadius: "25px",
  fontSize: "16px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};

const loadingStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100vh",
  fontSize: "1.5rem",
  color: "#666",
};
